import { useState, useEffect } from 'preact/hooks';
import { authState } from '../App';
import { api, type TeamMember } from '../services/api';
import { MemberInviteLinksSection } from '../components/MemberInviteLinksSection';

export function TeamPage() {
  const { user } = authState.value;
  const isInitialAdmin = (user as any)?.isInitialAdmin ?? false;
  const isAdmin = !!((user as any)?.isAdmin || user?.role === 'admin' || isInitialAdmin || (user as any)?.canInviteUsers);

  const [members, setMembers] = useState<TeamMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Per-member pending state (memberId → action in progress)
  const [pendingMember, setPendingMember] = useState<string | null>(null);

  // Invite form state
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName]   = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteSuccess, setInviteSuccess] = useState<string | null>(null);
  const [createdInviteUrl, setCreatedInviteUrl] = useState<string | null>(null);
  const [copiedSuccessUrl, setCopiedSuccessUrl] = useState(false);
  const [isInviting, setIsInviting] = useState(false);

  const loadMembers = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const result = await api.getTeamMembers();
      setMembers(result);
    } catch (err: any) {
      setError(err.message || 'Failed to load team members');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadMembers();
  }, []);

  const handleInvite = async (e: Event) => {
    e.preventDefault();
    setInviteError(null);
    setInviteSuccess(null);
    setCreatedInviteUrl(null);
    setIsInviting(true);
    try {
      const res = await api.inviteAdminUser({ firstName, lastName, email: inviteEmail });
      setInviteSuccess(`Invitation sent to ${inviteEmail}`);
      if (res.inviteUrl) {
        setCreatedInviteUrl(res.inviteUrl);
      }
      setFirstName('');
      setLastName('');
      setInviteEmail('');
      setShowInviteForm(false);
    } catch (err: any) {
      setInviteError(err.message || 'Failed to send invitation');
    } finally {
      setIsInviting(false);
    }
  };

  // Atomically toggle admin access — updates only the affected member in state
  const handleToggleAdminAccess = async (memberId: string, currentIsAdmin: boolean) => {
    if (pendingMember) return;
    setPendingMember(memberId);
    try {
      const res = await api.setAdminAccess(memberId, !currentIsAdmin);
      if (res.success && res.user) {
        setMembers(prev =>
          prev.map(m =>
            m.id === memberId
              ? { ...m, is_admin: res.user.is_admin, can_invite_users: res.user.can_invite_users }
              : m
          )
        );
      }
    } catch (err: any) {
      alert(err.message || 'Failed to update admin access');
    } finally {
      setPendingMember(null);
    }
  };

  // Atomically deactivate — flips only is_active on the affected member
  const handleDeactivate = async (memberId: string, name: string) => {
    if (pendingMember) return;
    if (!confirm(`Deactivate ${name}? They will no longer be able to log in.`)) return;
    setPendingMember(memberId);
    try {
      await api.deactivateTeamMember(memberId);
      setMembers(prev =>
        prev.map(m => (m.id === memberId ? { ...m, is_active: false } : m))
      );
    } catch (err: any) {
      alert(err.message || 'Failed to deactivate user');
    } finally {
      setPendingMember(null);
    }
  };

  // Permanently delete — removes the member from state after API call (Lead Admin only)
  const handleDelete = async (memberId: string, name: string) => {
    if (pendingMember) return;
    if (!confirm(`Permanently delete ${name}? This cannot be undone.`)) return;
    setPendingMember(memberId);
    try {
      await api.deleteTeamMember(memberId);
      setMembers(prev => prev.filter(m => m.id !== memberId));
    } catch (err: any) {
      alert(err.message || 'Failed to delete user');
    } finally {
      setPendingMember(null);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Admin Team</h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage the people who can access this church's dashboard.
          </p>
        </div>
        {isAdmin && (
          <button
            onClick={() => { setShowInviteForm(true); setInviteError(null); setInviteSuccess(null); }}
            className="btn-primary"
          >
            + Invite Admin
          </button>
        )}
      </div>

      {/* Success banner */}
      {inviteSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6 text-sm text-green-800">
          <div className="flex items-center justify-between">
            <span className="font-semibold flex items-center gap-1.5">
              <span>✓</span> {inviteSuccess}
            </span>
            <button
              onClick={() => { setInviteSuccess(null); setCreatedInviteUrl(null); }}
              className="text-green-600 hover:text-green-800 font-medium text-base"
            >
              ✕
            </button>
          </div>
          {createdInviteUrl && (
            <div className="mt-3 pt-3 border-t border-green-200">
              <p className="text-xs text-green-700 font-medium mb-1.5">
                Direct Member Invite Link (share via WhatsApp, SMS, or Slack):
              </p>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  readOnly
                  value={createdInviteUrl}
                  className="bg-white border border-green-300 rounded px-2.5 py-1 text-xs text-gray-700 font-mono w-full truncate select-all"
                  onClick={(e) => (e.target as HTMLInputElement).select()}
                />
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(createdInviteUrl);
                    } catch {
                      const textArea = document.createElement('textarea');
                      textArea.value = createdInviteUrl;
                      document.body.appendChild(textArea);
                      textArea.select();
                      document.execCommand('copy');
                      document.body.removeChild(textArea);
                    }
                    setCopiedSuccessUrl(true);
                    setTimeout(() => setCopiedSuccessUrl(false), 2000);
                  }}
                  className="px-3 py-1 bg-green-600 text-white rounded text-xs font-semibold hover:bg-green-700 flex-shrink-0"
                >
                  {copiedSuccessUrl ? 'Copied!' : 'Copy Link'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Invite form */}
      {showInviteForm && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Invite New Admin</h2>
          <form onSubmit={handleInvite} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                <input
                  type="text"
                  value={firstName}
                  onInput={(e) => setFirstName((e.target as HTMLInputElement).value)}
                  className="input"
                  placeholder="Jane"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                <input
                  type="text"
                  value={lastName}
                  onInput={(e) => setLastName((e.target as HTMLInputElement).value)}
                  className="input"
                  placeholder="Doe"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
              <input
                type="email"
                value={inviteEmail}
                onInput={(e) => setInviteEmail((e.target as HTMLInputElement).value)}
                className="input"
                placeholder="jane@example.com"
                required
              />
            </div>
            {inviteError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-600">
                {inviteError}
              </div>
            )}
            <div className="flex items-center gap-3">
              <button type="submit" disabled={isInviting} className="btn-primary disabled:opacity-50">
                {isInviting ? 'Sending…' : 'Send Invitation'}
              </button>
              <button
                type="button"
                onClick={() => setShowInviteForm(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Members table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">Loading…</div>
        ) : error ? (
          <div className="p-8 text-center text-red-600">{error}</div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role / Access</th>
                {isAdmin && (
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {members.map((member) => {
                const memberIsAdmin = !!(member.is_initial_admin || member.is_admin || member.can_invite_users);

                return (
                  <tr key={member.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-primary-700 font-medium text-sm">
                            {member.first_name.charAt(0)}{member.last_name.charAt(0)}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {member.first_name} {member.last_name}
                          </p>
                          {member.is_initial_admin && (
                            <span className="text-xs text-primary-600 font-medium">Original Admin</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {member.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        member.is_active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {member.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {member.is_initial_admin ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-800">
                          Lead Admin
                        </span>
                      ) : isAdmin ? (
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => handleToggleAdminAccess(member.id, memberIsAdmin)}
                            disabled={pendingMember === member.id}
                            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 disabled:opacity-50 ${
                              memberIsAdmin ? 'bg-primary-600' : 'bg-gray-200'
                            }`}
                            title={memberIsAdmin ? 'Revoke admin access' : 'Make Admin'}
                          >
                            <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                              memberIsAdmin ? 'translate-x-4' : 'translate-x-0.5'
                            }`} />
                          </button>
                          <span className={`text-xs font-medium ${memberIsAdmin ? 'text-primary-700 font-semibold' : 'text-gray-500'}`}>
                            {memberIsAdmin ? 'Admin' : 'Member'}
                          </span>
                        </div>
                      ) : (
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          memberIsAdmin ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {memberIsAdmin ? 'Admin' : 'Member'}
                        </span>
                      )}
                    </td>
                    {isAdmin && (
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                        {pendingMember === member.id ? (
                          /* Spinner */
                          <span className="inline-flex items-center justify-end">
                            <svg className="animate-spin h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                            </svg>
                          </span>
                        ) : (
                          <div className="flex items-center justify-end gap-1">
                            {/* Deactivate — ban/slash-circle icon */}
                            {!member.is_initial_admin && member.id !== user?.id && member.is_active && (
                              <button
                                onClick={() => handleDeactivate(member.id, `${member.first_name} ${member.last_name}`)}
                                disabled={!!pendingMember}
                                title={`Deactivate ${member.first_name} ${member.last_name}`}
                                className="p-1.5 rounded-md text-amber-500 hover:text-amber-700 hover:bg-amber-50 transition-colors disabled:opacity-40"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <circle cx="12" cy="12" r="10" />
                                  <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
                                </svg>
                              </button>
                            )}
                            {/* Permanent delete — trash icon (Lead Admin only) */}
                            {isInitialAdmin && !member.is_initial_admin && member.id !== user?.id && (
                              <button
                                onClick={() => handleDelete(member.id, `${member.first_name} ${member.last_name}`)}
                                disabled={!!pendingMember}
                                title={`Permanently delete ${member.first_name} ${member.last_name}`}
                                className="p-1.5 rounded-md text-red-500 hover:text-red-700 hover:bg-red-50 transition-colors disabled:opacity-40"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="3 6 5 6 21 6" />
                                  <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
                                  <path d="M10 11v6" />
                                  <path d="M14 11v6" />
                                  <path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2" />
                                </svg>
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    )}

                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Member Invite Links (Admin only) */}
      {isAdmin && (
        <div className="mt-8">
          <MemberInviteLinksSection />
        </div>
      )}
    </div>
  );
}
